import { atom } from 'jotai';
import { atomWithDefault, loadable } from 'jotai/utils';
import { IKPIRequestDataModel } from '../../../data-models/kpi-requests.data-model';
import { IKPITemplate } from '../../../data-models/kpi-template.data-model';
import { KPIGroup } from '../../../schemas/kpi-group.schema';
import { isConsolidatedTemplate } from '../../../schemas/kpi-template.schema';
import { FDMap } from '../../../util/data-structure/FDMap';
import { FMT } from '../../../util/formatter-service';
import { getKPIGroupsSettings } from '../../queries/KPIConfigQueries';
import { fetchAssignedKPIRequests } from '../../queries/KPIRequestsQueries';
import { fetchKPITemplates } from '../../queries/KPITemplatesQueries';
import { clientConfigFieldsAtom } from '../AppConfigStateJ';

type TemplatesValue = IKPITemplate[] | null | Promise<IKPITemplate[] | null>;

/**
 * Default fetches every KPI template (including deleted ones, all versions)
 * on first subscribe. Consumers using `useAtomValue` will suspend on first
 * read; use {@link kpiTemplatesListStateLoadableJ} for non-suspending callers.
 */
export const kpiTemplatesListStateJ = atomWithDefault<TemplatesValue>(async () => {
  return (await fetchKPITemplates()) as IKPITemplate[];
});

export const kpiTemplatesListStateLoadableJ = loadable(kpiTemplatesListStateJ);

/**
 * Async map of template uuid → latest-version template, derived from
 * {@link kpiTemplatesListStateJ}. Side-effect: registers the
 * `kpiTemplateName` formatter so display helpers can resolve a uuid to a name.
 *
 * FIXME MAGGIE-6902 — the side effect inside a derived atom is a smell worth
 * revisiting once formatters get their own data source.
 */
export const kpiTemplatesMapByUuidStateJ = atom(async (get): Promise<Map<string, IKPITemplate>> => {
  const templates = await get(kpiTemplatesListStateJ);
  const map = new Map<string, IKPITemplate>();
  if (templates) {
    templates.forEach((template) => {
      const existing = map.get(template.uuid);
      if (!existing) {
        map.set(template.uuid, template);
      } else {
        const latest =
          (existing.version ?? Number.MIN_SAFE_INTEGER) > (template.version ?? Number.MIN_SAFE_INTEGER)
            ? existing
            : template;
        map.set(template.uuid, latest);
      }
    });
  }

  FMT.get().setFormatterForId('kpiTemplateName', (formatterId: string) => {
    return map.get(formatterId)?.name ?? formatterId;
  });

  return map;
});

export const kpiTemplatesMapByUuidStateLoadableJ = loadable(kpiTemplatesMapByUuidStateJ);

const kpiGroupsBaseAtom = atom<KPIGroup[] | null>(null);

export const kpiGroupsState = atom(
  async (get) => {
    const existing = get(kpiGroupsBaseAtom);
    if (existing !== null) {
      return existing;
    }
    const res = await getKPIGroupsSettings();
    res.sort((s1, s2) => s1.sortOrder - s2.sortOrder);
    return res;
  },
  (_get, set, value: KPIGroup[]) => {
    set(kpiGroupsBaseAtom, value);
  }
);

export const kpiGroupsByIdState = atom(async (get) => {
  const groups = await get(kpiGroupsState);

  return groups.reduce((res, group) => {
    return res.set(group.id, group);
  }, new Map<number, KPIGroup>());
});

/**
 * Templates matching the client's current mode (consolidated vs not). Reactive
 * to both {@link kpiTemplatesListStateJ} and the AppConfig
 * `Using_Consolidated_Data_Collection` flag.
 */
export const kpiActiveTemplatesListStateJ = atom(async (get): Promise<IKPITemplate[] | null> => {
  const templates = await get(kpiTemplatesListStateJ);
  if (!templates) {
    return null;
  }
  const isConsolidated = get(clientConfigFieldsAtom)?.Using_Consolidated_Data_Collection;
  return templates.filter((t) => (isConsolidated ? isConsolidatedTemplate(t) : !isConsolidatedTemplate(t)));
});

export const kpiActiveTemplatesListStateLoadableJ = loadable(kpiActiveTemplatesListStateJ);

/**
 * Fetches all assigned KPI requests once, then groups them by company id,
 * sorted by `sentAt` descending.
 */
export const assignedKPIRequestsStateByCompanyJ = atomWithDefault<
  FDMap<number, IKPIRequestDataModel[]> | Promise<FDMap<number, IKPIRequestDataModel[]>>
>(async () => {
  const allRequests = await fetchAssignedKPIRequests();
  return allRequests
    .sort((a, b) => new Date(b.sentAt!).getTime() - new Date(a.sentAt!).getTime())
    .reduce((byCompany, request) => {
      return byCompany.setOrUpdate(request.companyId, [request], (value) => {
        value.push(request);
        return value;
      });
    }, new FDMap<number, IKPIRequestDataModel[]>());
});
